import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderContextValue {
  title: string;
  breadcrumbs: BreadcrumbItem[];
  actions: ReactNode;
  setTitle: (title: string) => void;
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  setActions: (actions: ReactNode) => void;
  setPageHeader: (config: {
    title: string;
    breadcrumbs: BreadcrumbItem[];
    actions?: ReactNode;
  }) => void;
  clearPageHeader: () => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | undefined>(undefined);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [actions, setActions] = useState<ReactNode>(null);

  const setPageHeader = useCallback(
    (config: { title: string; breadcrumbs: BreadcrumbItem[]; actions?: ReactNode }) => {
      setTitle(config.title);
      setBreadcrumbs(config.breadcrumbs);
      setActions(config.actions ?? null);
    },
    [],
  );

  const clearPageHeader = useCallback(() => {
    setTitle('');
    setBreadcrumbs([]);
    setActions(null);
  }, []);

  return (
    <PageHeaderContext.Provider
      value={{
        title,
        breadcrumbs,
        actions,
        setTitle,
        setBreadcrumbs,
        setActions,
        setPageHeader,
        clearPageHeader,
      }}
    >
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeader() {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider');
  }
  return context;
}
